import { skipToken } from '@reduxjs/toolkit/query';
import Button from '@rio-cloud/rio-uikit/Button';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import { useListEligibleTransportOrderVehiclesQuery } from 'api/transportOrdersApi';

import type { DateTimeRange } from './DateTimeRangeField';
import { isRangeComplete } from './transportOrderFormModel';
import styles from './VehicleAssignment.module.scss';

const chipColorClass = (isSelected: boolean): string =>
    isSelected ? 'bg-coldplay-kashmir' : 'bg-white text-color-dark';

export type VehicleAssignmentProps = {
    pickupWindow: DateTimeRange;
    deliveryWindow: DateTimeRange;
    selectedVin: string;
    onSelect: (vin: string) => void;
    excludeTransportOrderId?: string;
};

const VehicleAssignment = ({
    pickupWindow,
    deliveryWindow,
    selectedVin,
    onSelect,
    excludeTransportOrderId,
}: VehicleAssignmentProps) => {
    const windowsComplete = isRangeComplete(pickupWindow) && isRangeComplete(deliveryWindow);

    const { data: vehicles = [], isFetching } = useListEligibleTransportOrderVehiclesQuery(
        windowsComplete
            ? {
                  pickupFrom: pickupWindow.from,
                  pickupTo: pickupWindow.to,
                  deliveryFrom: deliveryWindow.from,
                  deliveryTo: deliveryWindow.to,
                  excludeTransportOrderId,
              }
            : skipToken
    );

    if (!windowsComplete) {
        return (
            <p className='text-color-light margin-bottom-0'>
                Fill in pickup and delivery dates to see available vehicles
            </p>
        );
    }

    if (isFetching) {
        return <Spinner text='Finding available vehicles' />;
    }

    if (vehicles.length === 0) {
        return <p className='text-color-light margin-bottom-0'>No vehicles are available for these dates.</p>;
    }

    return (
        <div className='display-flex flex-wrap gap-10' role='radiogroup' aria-label='Available vehicles'>
            {vehicles.map(vehicle => {
                const isSelected = selectedVin === vehicle.vin;

                return (
                    <Button
                        key={vehicle.vin}
                        asToggle
                        active={isSelected}
                        bsStyle={Button.DEFAULT}
                        bsSize={Button.SM}
                        iconName='icon-truck'
                        className={`${styles.vehicle} ${chipColorClass(isSelected)}`}
                        role='radio'
                        aria-checked={isSelected}
                        aria-label={vehicle.name}
                        onClick={() => onSelect(vehicle.vin)}
                    >
                        {vehicle.name}
                    </Button>
                );
            })}
        </div>
    );
};

export default VehicleAssignment;
